import { supabase } from '@/integrations/supabase/client';
import { dispatchNotification } from '@/services/notificationDispatcher';
import type { Database } from '@/integrations/supabase/types';

type NotificationType = Database['public']['Enums']['notification_type'];

export interface OrderItem {
  product_id: string;
  quantity: number;
  price: number;
}

export interface CreateOrderData {
  userId: string | null;
  customerEmail: string;
  customerName: string;
  totalAmount: number;
  paymentMethod: string;
  shippingAddress: string;
  billingAddress: string;
  cartItems: OrderItem[];
}

export interface OrderResult {
  success: boolean;
  orderId?: string;
  orderNumber?: string;
  error?: string;
}

export interface Order {
  id: string;
  user_id: string | null;
  total_amount: number;
  status: string | null;
  payment_status: string | null;
  payment_method: string | null;
  created_at: string | null;
  updated_at: string | null;
  tracking_number?: string | null;
}

export const createOrder = async (orderData: CreateOrderData): Promise<OrderResult> => {
  try {
    console.log('Creating order with data:', orderData);

    const { data: order, error } = await supabase.rpc('create_order_with_items', {
      p_user_id: orderData.userId,
      p_customer_email: orderData.customerEmail,
      p_customer_name: orderData.customerName,
      p_total_amount: orderData.totalAmount,
      p_payment_method: orderData.paymentMethod,
      p_shipping_address: JSON.parse(orderData.shippingAddress),
      p_billing_address: JSON.parse(orderData.billingAddress),
      p_cart_items: orderData.cartItems as any,
    });

    if (error) {
      console.error('Error creating order:', error);
      throw new Error(error.message || 'Failed to create order');
    }

    if (!order) {
      throw new Error('No order ID returned from database');
    }

    console.log('Order created successfully:', order);

    // Fetch complete order details with items for email
    const { data: fullOrder } = await supabase
      .from('orders')
      .select(`
        *,
        order_items (
          id,
          quantity,
          price,
          products (
            id,
            name,
            image_url
          )
        )
      `)
      .eq('id', order)
      .single();

    // Format items for email template
    const items = fullOrder?.order_items?.map((item: any) => ({
      name: item.products?.name || 'Product',
      quantity: item.quantity,
      price: item.price,
      image: item.products?.image_url,
      total: (item.quantity * item.price).toFixed(2)
    })) || [];

    // Dispatch notifications using the new system
    await dispatchNotification('order_created' as NotificationType, {
      orderId: order,
      orderNumber: fullOrder?.order_number || `ORD-${order.slice(0, 8)}`,
      orderDate: new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      customerName: orderData.customerName,
      customerEmail: orderData.customerEmail,
      totalAmount: orderData.totalAmount,
      items,
      shippingAddress: JSON.parse(orderData.shippingAddress),
      estimatedDelivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long', 
        day: 'numeric'
      })
    }, orderData.userId, orderData.totalAmount >= 500 ? 'high' : 'medium');

    return {
      success: true,
      orderId: order,
      orderNumber: `ORD-${order.slice(0, 8)}`
    };
  } catch (error: any) {
    console.error('Order creation failed:', error);
    return {
      success: false,
      error: error.message || 'Failed to create order'
    };
  }
};

export const getOrderById = async (orderId: string): Promise<any> => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('id', orderId)
      .single();

    if (error) throw error;
    return data;
  } catch (error: any) {
    console.error('Error fetching order:', error);
    throw error;
  }
};

export const getOrderByNumber = async (orderNumber: string): Promise<any> => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('order_number', orderNumber)
      .single();

    if (error) throw error;
    return data;
  } catch (error: any) {
    console.error('Error fetching order:', error);
    throw error;
  }
};

export const updateOrderStatus = async (orderId: string, status: string): Promise<any> => {
  try {
    const { data: order, error } = await supabase
      .from('orders')
      .update({ 
        status,
        updated_at: new Date().toISOString() 
      })
      .eq('id', orderId)
      .select('*, profiles(full_name, email)')
      .single();

    if (error) throw error;

    const customerName = order.profiles?.full_name || order.customer_name;

    // Dispatch status-specific notifications
    switch (status) {
      case 'shipped':
        await dispatchNotification('order_updated' as NotificationType, {
          orderId: order.id,
          orderNumber: order.order_number,
          newStatus: 'shipped',
          status: 'shipped',
          customerName,
          trackingNumber: order.tracking_number || 'TRK-' + order.id.slice(0, 10).toUpperCase(),
          carrier: 'USPS',
          estimatedDelivery: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        }, order.user_id, 'high');
        break;

      case 'delivered':
        await dispatchNotification('order_updated' as NotificationType, {
          orderId: order.id,
          orderNumber: order.order_number,
          newStatus: 'delivered',
          status: 'delivered',
          customerName
        }, order.user_id, 'high');
        break;

      case 'cancelled':
        await dispatchNotification('order_updated' as NotificationType, {
          orderId: order.id,
          orderNumber: order.order_number,
          newStatus: 'cancelled',
          status: 'cancelled',
          customerName,
          refundAmount: order.total_amount,
          refundMethod: order.payment_method || 'original payment method'
        }, order.user_id, 'high');
        break;

      default:
        // Generic order update for other statuses
        await dispatchNotification('order_updated' as NotificationType, {
          orderId: order.id,
          orderNumber: order.order_number,
          newStatus: status,
          status,
          customerName
        }, order.user_id);
    }

    return order;
  } catch (error: any) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

export const bulkUpdateOrderStatus = async (orderIds: string[], status: string): Promise<any> => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .update({ status })
      .in('id', orderIds);

    if (error) throw error;
    return data;
  } catch (error: any) {
    console.error('Error updating order statuses:', error);
    throw error;
  }
};
